import React, {useEffect, useRef, useState} from 'react';
import styles from '../styles/InstanceDetailsModal.module.css';
import axiosInstance from "../utility/axiosInstance";
import {Instance} from "../store/slices/instancesSlice";
import {useSelector} from 'react-redux';
import {RootState} from '../store/store';
import {useToast} from './ToastContext';
import {FaRegCopy, FaCheck} from 'react-icons/fa';
import {formatUptime, formatAge} from './InstanceRow';
import { formatRam } from '../utility/flavorOptions';

interface Props {
    open: boolean;
    onClose: () => void;
    instance: Instance | null;
}

interface Volume {
    id: string;
    name: string;
    status: string;
    size: number;
    bootable: string | boolean;
    created_at: any;
    device?: string;
    volume_type?: string;
}

interface ActionLogEntry {
    request_id: string;
    action: string;
    start_time: string;
}

// Exported for the free VM details (FreeVmDetailsModal), which read like these.
export const CopyButton: React.FC<{ text: string }> = ({ text }) => {
    const [copied, setCopied] = useState(false);
    return (
        <button
            onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            title="Copy"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#22c55e' : 'var(--text-muted)', padding: '2px 4px', borderRadius: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
        >
            {copied ? <FaCheck size={12} /> : <FaRegCopy size={12} />}
        </button>
    );
};

const InstanceDetailsModal: React.FC<Props> = ({open, onClose, instance}) => {
    const {showToast} = useToast();
    const [activeTab, setActiveTab] = useState('details');
    const [volumes, setVolumes] = useState<Volume[]>([]);
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState<string>('');
    const [logsLoading, setLogsLoading] = useState(false);
    const [actionLogs, setActionLogs] = useState<ActionLogEntry[]>([]);
    const [loadingActionLogs, setLoadingActionLogs] = useState(false);
    const [sshPort, setSshPort] = useState<number | null>(null);
    const [loadingPort, setLoadingPort] = useState(false);

    const [uptimeDisplay, setUptimeDisplay] = useState<string>('-');
    const uptimeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const flavors = useSelector((state: RootState) => state.flavors.data);
    const images = useSelector((state: RootState) => state.images.data);

    const getFlavor = (id: string) => flavors.find(f => f.id === id);
    const getImage = (id: string) => images.find(img => img.id === id);

    useEffect(() => {
        if (!instance?.id) return;

        if (activeTab === 'volume') {
            fetchVolumes(instance.id);
        } else if (activeTab === 'logs') {
            fetchLogs(instance.id);
        } else if (activeTab === 'actionlog') {
            fetchActionLogs(instance.id);
        }
    }, [activeTab, instance?.id]);

    useEffect(() => {
        if (!instance) return;

        const floatingIp = Object.values(instance.addresses ?? {})
            .flat()
            .find((addr: any) => addr["OS-EXT-IPS:type"] === "floating")?.addr;

        if (!floatingIp) return;

        fetchPort(floatingIp);
    }, [instance]);

    useEffect(() => {
        if (uptimeIntervalRef.current) clearInterval(uptimeIntervalRef.current);
        if (!instance) return;

        const tick = () => setUptimeDisplay(formatUptime(instance.launched_at, instance.status));
        tick();
        uptimeIntervalRef.current = setInterval(tick, 1000);
        return () => { if (uptimeIntervalRef.current) clearInterval(uptimeIntervalRef.current); };
    }, [instance?.launched_at, instance?.status]);

    const fetchVolumes = async (instanceId: string) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/instances/${instanceId}/volumes`);
            setVolumes(res.data || []);
        } catch (err) {
            console.error('Error fetching volumes:', err);
            setVolumes([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchLogs = async (instanceId: string) => {
        setLogsLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/instances/${instanceId}/logs`);
            setLogs(res.data.output || 'No logs found.');
        } catch (err) {
            console.error('Error fetching logs:', err);
            setLogs('Failed to load logs.');
        } finally {
            setLogsLoading(false);
        }
    };

    const fetchActionLogs = async (instanceId: string) => {
        setLoadingActionLogs(true);
        try {
            const res = await axiosInstance.get(`/v1/instances/${instanceId}/actionlog`);
            setActionLogs(res.data || []);
        } catch (err) {
            console.error('Error fetching action logs:', err);
            setActionLogs([]);
        } finally {
            setLoadingActionLogs(false);
        }
    };


    const fetchPort = async (floatingIp: any) => {
        // Try metadata first
        if (instance?.metadata?.forwardedSSHPort) {
            setSshPort(parseInt(instance.metadata.forwardedSSHPort));
            return;
        }

        setLoadingPort(true);
        try {
            const response = await axiosInstance.get('/v1/nginx/used-ports');
            const data = response.data;

            if (!data.routes || !Array.isArray(data.routes)) {
                console.error('Invalid response format:', data);
                return;
            }

            const route = data.routes.find((r: any) => r.targetIp === floatingIp && r.port);
            if (route) {
                setSshPort(route.port);
            }
        } catch (err) {
            console.error("Error fetching SSH port:", err);
        } finally {
            setLoadingPort(false);
        }
    };

    if (!open || !instance) return null;

    const flavor = getFlavor(instance.flavor_id);
    const image = getImage(instance.image_id);

    const host = instance.metadata?.forwardedSSHHost || (instance.dns?.includes(":")
        ? instance.dns.split(":")[1]
        : instance.dns);

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2>Instance: {instance.name}</h2>
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                        <button onClick={onClose} className={styles.closeButton}>×</button>
                    </div>
                </div>

                <div className={styles.tabHeader}>
                    {['details', 'flavor', 'image', 'volume', 'logs', 'actionlog'].map(tab => (
                        <button
                            key={tab}
                            className={activeTab === tab ? styles.activeTab : ''}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                <div className={styles.tabContent}>
                    {activeTab === 'details' && (
                        <div className={styles.section}>
                            <h4>Instance Details</h4>

                            <ul>
                                <li><strong>ID:</strong> {instance.id}</li>
                                <li><strong>Created At:</strong> {new Date(instance.created).toLocaleString()}</li>
                                <li><strong>Age:</strong> {formatAge(instance.created)}</li>
                                <li><strong>Uptime:</strong> {uptimeDisplay}</li>
                                <li>
                                    <strong>Network:</strong> {Object.keys(instance.addresses ?? {}).join(', ') || 'N/A'}
                                </li>
                                {Object.entries(instance.addresses ?? {}).flatMap(([netName, ips]) =>
                                    ips
                                        .filter(ip => ip["OS-EXT-IPS:type"] === "fixed")
                                        .map((ip, i) => (
                                            <li key={`fixed-${netName}-${i}`}>
                                                <strong>Internal IP</strong>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{ip.addr}</code>
                                                    <CopyButton text={ip.addr} />
                                                </span>
                                            </li>
                                        ))
                                )}

                                <li>
                                    <strong>SSH Port:</strong>{" "}
                                    {loadingPort ? "Loading..." : sshPort ?? "N/A"}
                                </li>
                                {host && <li><strong>Public address:</strong> <code>{host}</code></li>}

                                <li><strong>Image Name:</strong> {image?.name || instance.metadata?.imageName || 'N/A'}
                                </li>
                                <li><strong>Security Groups:</strong> {instance.security_groups?.join(', ') || 'N/A'}
                                </li>
                                <li><strong>SSH Keypair:</strong> {instance.key_name ?? '-'}</li>

                                {/*{Object.entries(instance.metadata || {})*/}
                                {/*    .filter(([key]) => !['dns', 'volume', 'isPublic', 'enableSSH', 'image', 'imageName', 'dName', 'dname', 'SSHEnabled'].includes(key))*/}
                                {/*    .map(([key, value]) => (*/}
                                {/*        <li key={key}><strong>{key}:</strong> {value}</li>*/}
                                {/*    ))*/}
                                {/*}*/}
                            </ul>
                        </div>
                    )}

                    {activeTab === 'flavor' && flavor && (
                        <div className={styles.section}>
                            <h4>Flavor Info</h4>
                            <ul>
                                <li><strong>Name:</strong> {flavor.name}</li>
                                <li><strong>vCPUs:</strong> {flavor.vcpus}</li>
                                <li><strong>RAM:</strong> {formatRam(flavor.ram)}</li>
                                <li><strong>Disk:</strong> {flavor.disk} GB</li>
                            </ul>
                        </div>
                    )}

                    {activeTab === 'image' && (
                        <div className={styles.section}>
                            <h4>Image Info</h4>
                            {image ? (
                                <ul>
                                    <li><strong>Name:</strong> {image.name}</li>
                                    <li><strong>ID:</strong> {image.id}</li>
                                </ul>
                            ) : (
                                <p>Image details not available (ID: {instance.image_id})</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'volume' && (
                        loading ? (
                            <p>Loading volumes...</p>
                        ) : volumes.length === 0 ? (
                            <p>No volumes attached to this instance.</p>
                        ) : (
                            <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                                {volumes.map((vol) => (
                                    <div key={vol.id} className={styles.section}>
                                        <h4>Volume: {vol.name || (String(vol.bootable) === "true" ? "boot-volume" : "N/A")}</h4>
                                        <ul>
                                            <li><strong>ID:</strong> {vol.id}</li>
                                            <li><strong>Status:</strong> {vol.status}</li>
                                            <li><strong>Size:</strong> {vol.size} GB</li>
                                            <li><strong>Device:</strong> {vol.device || 'N/A'}</li>
                                            <li><strong>Type:</strong> {vol.volume_type || 'N/A'}</li>
                                            <li><strong>Bootable:</strong> {String(vol.bootable)}</li>
                                            <li><strong>Created:</strong> {new Date(vol.created_at).toLocaleString()}
                                            </li>
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {activeTab === 'logs' && (
                        <div className={styles.section}>
                            <h4>Instance Console Logs</h4>
                            {logsLoading ? (
                                <p>Loading logs...</p>
                            ) : (
                                <pre style={{maxHeight: '400px', overflowY: 'auto', whiteSpace: 'pre-wrap'}}>
                {logs}
            </pre>
                            )}
                        </div>
                    )}

                    {activeTab === 'actionlog' && (
                        <div className="datatablenoactions">
                            {loadingActionLogs ? (
                                <p>Loading action log...</p>
                            ) : actionLogs.length === 0 ? (
                                <p>No actions recorded.</p>
                            ) : (
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>Request ID</th>
                                            <th>Action</th>
                                            <th>Start Time</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {actionLogs.map((action) => (
                                            <tr key={action.request_id}>
                                                <td>{action.request_id}</td>
                                                <td>{action.action}</td>
                                                <td>{new Date(action.start_time).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InstanceDetailsModal;
